import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridProProps, GridPaginationModel, GridRowParams } from '@mui/x-data-grid-pro';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { IGroupSFilter } from 'src/@types/SearchFilterTypes';
import CDataGridPro from 'src/components/CDataGridPro';
import { CGridFooter } from 'src/components/custom-grid-data-components';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol, { IGroupGridRow } from 'src/hooks/useGetGridCol';
import { ContentBox, ContentBoxButtons, ContentButtonCreate } from 'src/layouts/form/FormButton';
import { IGroup, useStores } from 'src/models';
import { INIT_PAGINATION, IPagination } from 'src/models/extensions/withPagination';
import { groupApi } from 'src/service';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';

import GroupFormDialog from './GroupFormDialog';

interface IGroupGridProps extends Omit<DataGridProProps, 'columns'> {
  searchData?: Partial<IGroupSFilter>;
}

export const GroupGrid = ({ searchData, ...props }: IGroupGridProps) => {
  const { t } = useTranslation();
  const { alertStore } = useStores();
  const { callApiWithLoading } = useCallApiWithLoading();
  const { openDialog, closeDialog } = useDialogContext();
  const { getGroupGridCol } = useGetGridCol();
  const [gridRows, setGridRows] = useState<IGroupGridRow[]>([]);
  const [pagination, setPagination] = useState<IPagination>(INIT_PAGINATION);

  // 목록 조회
  const getGroupData = useCallback(async () => {
    const pageParams = {
      ...searchData,
      page: pagination.page,
      size: pagination.size,
    };
    const result = await groupApi.getGroups(pageParams);
    if (result) {
      const newGridRows = result.data.map((group: IGroup, i: number) => {
        return { ...group, id: group.oprtrGroupSid ?? i };
      });
      setGridRows(newGridRows);
      if (result.pagination) {
        setPagination(result.pagination);
      }
    }
  }, [searchData, pagination.page, pagination.size]);

  useEffect(() => {
    getGroupData();
  }, [getGroupData, pagination.page, pagination.size]);

  // 등록 form dialog: 등록 버튼 선택 시
  const handleAdd = () => {
    openDialog({
      id: FORM_IDS.GROUP,
      formType: FormType.CREATE,
      maxWidth: 'sm',
      dialogContent: (
        <GroupFormDialog formType={FormType.CREATE} onSubmitSuccess={handleSubmitSuccess} />
      ),
    });
  };

  // 그리드 더블 클릭 시: 수정 dialog open
  const handleRowDoubleClick = (param: GridRowParams<IGroupGridRow>) => {
    callApiWithLoading(async () => {
      await groupApi.getDetail(param.row.oprtrGroupSid).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.GROUP,
            formType: FormType.UPDATE,
            maxWidth: 'sm',
            dialogContent: (
              <GroupFormDialog
                formType={FormType.UPDATE}
                selectedGroup={res.data}
                onSubmitSuccess={handleSubmitSuccess}
              />
            ),
          });
        }
      });
    });
  };

  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.GROUP);
    getGroupData();
  };

  // 페이지 모델 변경 시
  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    const newPagination = {
      ...pagination,
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
    };
    setPagination(newPagination);
  };
  return (
    <ContentBox>
      <ContentBoxButtons>
        <ContentButtonCreate onClick={handleAdd}>{t('common.button.register')}</ContentButtonCreate>
      </ContentBoxButtons>

      <CDataGridPro
        columns={getGroupGridCol()}
        rows={gridRows}
        rowCount={toJS(pagination.totalElements)}
        paginationModel={{
          page: pagination.page - 1,
          pageSize: pagination.size,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        slots={{ footer: CGridFooter }}
        onRowDoubleClick={handleRowDoubleClick}
        isRowSelectable={(params) => !params.row.wthdYn}
        {...props}
      />
    </ContentBox>
  );
};

export default observer(GroupGrid);
